import { move } from '@dnd-kit/helpers';
import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import { isSortableOperation } from "@dnd-kit/react/sortable";
import { Button, Divider, InputNumber, Popconfirm } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Footer } from '..';
import Note from "../../../../../components/Popup/PopupOpInfo/_component/Note";
import { selectTenantId } from "../../../../../util/store/selectors";
import { getStore } from "../../../../../util/store/store";
import {
    ACTION_TYPE,
    CERTAIN_TYPE,
    OPERATION_TYPE,
    PROCESS_TYPE,
    TASK_TYPE,
    taskTypes,
    treatmentType,
    VESSEL_TYPE,
} from "../../../../../util/varibles/constants";
import { durationTxt } from '../../../../../util/varibles/global';
import { IOperation, IOperations, IUnit, TOperation, TTaskTreatment } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import { UnitModel } from "../../../BiologyBrowser/constants";
import { selectPlan } from '../../../constants';
import { planOpActions } from '../../../reducer';
import AddChecklist from '../_components/AddChecklist';
import AddNote from '../_components/AddNote';
import AddUnit from '../_components/AddUnit';
import AttachFile from '../_components/AttachFile';
import Files from '../_components/AttachFile/Files';
import Certain from '../_components/Certain';
import Cleaning from '../_components/Cleaning';
import Duration from '../_components/Duration';
import Invoices from '../_components/Invoices';
import SelectTime from '../_components/SelectTime';
import { callEventEditOp, getEditOp, IUnitWhenEdit, setEditOp, useEditOp } from "../EditOperationContext";
import { RoundMapProvider } from '../EditOperationContext/RoundMapContext';
import { checkSorting, checkTreatment, initTask, normalizeParallelGroups } from "./constants";
import {
    findTaskById,
    findTaskUnitId,
    flattenTaskContainers,
    rebuildUnitsFromContainers,
    TreatmentDragContainers,
} from './dragDrop';
import RoundMap from './RoundMap';
import styles from './style.module.scss';
import GroupByUnit from "./Unit";



const DensityUnits: React.FC = () => {
    const [densityPercent, setDensityPercent] = useState<number | null>(100);
    const [focus, setFocus] = useState(false);
    const set = setEditOp();

    const handleDensity = useCallback(() => {
        set(({ units }) => {
            return {
                units: Object.keys(units).reduce((rs: { [id: string]: IUnitWhenEdit }, key) => {
                    const { tank_number, ...args } = units[key];
                    rs[key] = { ...args, density_percent: Math.round(densityPercent || 100) / 100 }

                    return rs;
                }, {}),
                isCalculate: true
            }
        })
    }, [densityPercent, set]);

    return <Popconfirm
        title={<div className='mx-[10px] text-[15px]'>
            Update density for all units
        </div>}
        icon={false}
        description={<div className='flex items-center gap-[8px] mx-[10px] my-[6px]'>
            <div>Density:</div>
            <InputNumber<number>
                defaultValue={100}
                min={0}
                value={densityPercent ? Math.round(densityPercent) : densityPercent}
                formatter={(value) => focus ? `${value}` : `${value}%`}
                parser={(value) => value?.replace('%', '') as unknown as number}
                onChange={value => setDensityPercent((value))}
                onFocus={() => setFocus(true)}
                onBlur={() => {
                    setFocus(false);
                    densityPercent === null && setDensityPercent(100)
                }}
            />
        </div>}
        onConfirm={handleDensity}
        okText="Save"
        cancelText="Cancel"
    >
        <Button className='bt-ghost' data-border={false} data-icon={true}>
            <div className='px-[8px]'>Density</div>
        </Button>
    </Popconfirm>
}

let control: AbortController;

let currentUnitId: string | undefined;

const getTaskDragLabel = (task: TTaskTreatment) => {
    const { type, treatment_type, destination_name = '', type_name = '' } = task;
    let taskName = taskTypes[type || '']?.name || 'Task';

    if (type === TASK_TYPE.TREATMENT) {
        taskName = treatmentType[treatment_type || '']?.name || taskName;
    } else if (type === TASK_TYPE.COMBINE) {
        taskName = type_name || taskName;
    } else if (!taskName && type === TASK_TYPE.MOVE) {
        taskName = destination_name || 'Task';
    }

    const duration = task.duration ? durationTxt(task.duration).trim() : '';
    return duration ? `${taskName} (${duration})` : taskName;
};

const Treatment: React.FC = () => {
    const [isDraggingUnit, setIsDraggingUnit] = useState(false);
    const [dragSourceUnitId, setDragSourceUnitId] = useState<string | null>(null);
    const [dragPreviewUnits, setDragPreviewUnits] = useState<Record<string, IUnitWhenEdit> | null>(null);
    const dragPreviewUnitsRef = useRef<Record<string, IUnitWhenEdit> | null>(null);
    const sites = useSelector(selectPlan.sites);
    const operations = useSelector(selectPlan.ops);
    const get = getEditOp();
    const { getRoundMap, handeUpdateOp, handleSave, turnOnCalculate } = callEventEditOp();
    const getRoundMapRef = useRef(getRoundMap);
    const handeUpdateOpRef = useRef(handeUpdateOp);
    const handleSaveRef = useRef(handleSave);
    const dispatch = useDispatch();
    const [isCalculate, set] = useEditOp(state => state.isCalculate)

    useEffect(() => {
        getRoundMapRef.current = getRoundMap;
        handeUpdateOpRef.current = handeUpdateOp;
        handleSaveRef.current = handleSave;
    }, [getRoundMap, handeUpdateOp, handleSave]);

    const handleGetRoundMap = useCallback((...args: Parameters<typeof getRoundMap>) => {
        getRoundMapRef.current(...args);
    }, []);

    const handleUpdateOp = useCallback((...args: Parameters<typeof handeUpdateOp>) => {
        handeUpdateOpRef.current(...args);
    }, []);

    const getActivityLogOfTreatment = (callback?: (newOp?: IOperation) => void) => {
        const { operation, units, files, vessel, activity_log, editMode } = get()
        let newSubOperations: IUnit[] = [], error = false, newTasks: TTaskTreatment[] = [];
        const newUnits = Object.keys(units).reduce((rs: { [id: string]: IUnitWhenEdit }, key) => {
            const { tasks = [], ...args } = units[key];
            if (tasks.length > 0) {
                if (!args.fish_amount || !args.total_weight) {
                    notify.error(NotifyCode.E40)([args.unit_id]);
                    error = true;
                    return rs;
                }

                newSubOperations.push(args)
                const { isError, tasks: tasksAfterCheck } = checkTreatment(tasks as TTaskTreatment[]);
                if (isError) {
                    error = true;
                    rs[key] = { ...units[key], tasks: tasksAfterCheck }
                } else
                    newTasks = [...newTasks, ...tasks] as TTaskTreatment[]
            }
            return rs
        }, {});

        if (error) {
            set(({ units: prev }) => ({ units: ({ ...prev, ...newUnits }) }))
            return;
        }
        const { site_id = '', site_name = '' } = newSubOperations[0];
        const data = {
            operation: {
                ...operation,
                site_id,
                site_name,
                operation_type: OPERATION_TYPE.TREATMENT,
                tasks: newTasks,
                sub_operations: newSubOperations,
                files: files.map((item: any) => ({ key: item.key, name: item.name })),
            } as TOperation,
            vessel,
            activity_log
        } as IOperation;
        const { id: opId } = data.operation;
        if (control)
            control.abort();
        control = new AbortController();
        set({ loadingSubmit: true });
        const { startTime } = get();
        dispatch(planOpActions.getActivityLog({
            source: [data],
            skipDurationEst: editMode === ACTION_TYPE.CREATE,
            properties: { [opId]: { start: startTime } },
            signal: control,
            success: (ops: IOperations) => {
                const { operation, activity_log } = ops[opId];
                set({ loadingSubmit: false, operation, activity_log });
                const newOp = { ...data, operation, activity_log };
                callback?.(newOp)
            },
            failure: () => {
                set({ loadingSubmit: false });
            }
        }))
    }

    const handleSubmit = () => {
        if (get().loadingRoundMap)
            return;

        const { operation, units, files, isCalculate } = get()
        const { id: opId = "" } = operation || {};
        const listOfFile = files.length > 0 ? { [opId]: files } : {};
        if (!isCalculate) {
            const data = {
                ...operations[opId],
                operation: {
                    ...operation,
                    files: files.map((item: any) => ({ key: item.key, name: item.name })),
                    sub_operations: Object.keys(units).reduce((rs: IUnit[], key: string) => {
                        const { tasks = [], ...argsUnit } = units[key];
                        if (tasks.length > 0)
                            return [...rs, argsUnit]
                        return rs;
                    }, [])
                }
            } as IOperation;
            handleSaveRef.current({ update: { [opId]: data }, isCheck: false, files: listOfFile });
            return;
        } else {
            const { editMode } = get();
            getActivityLogOfTreatment(newOp => {
                if (newOp) {
                    const value = editMode === ACTION_TYPE.EDIT ? { update: { [opId]: newOp } } : { add: { [opId]: newOp } }
                    handleSaveRef.current({ ...value, files: listOfFile });
                }
            })
        }
    }

    useEffect(() => {
        const el = document.getElementById('container-treatment');
        if (el)
            setTimeout(() => el.scrollTo(0, 0), 100);

        const popupEdit = selectPlan.popupEdit(getStore());
        const { values, editMode } = popupEdit || {};
        const { store = {} } = values || {};
        const { operation, activity_log } = get();
        const [{ site_id: newSiteId, site_name: newSiteName }] = operation?.sub_operations || [{}];
        const site = sites.find(site => site.id === newSiteId);
        if (!site)
            return;
        const userTenantId = selectTenantId(getStore());
        const {
            site_id = newSiteId,
            tenant_id = userTenantId,
            site_name = newSiteName,
            certain_mode = CERTAIN_TYPE.NONE,
            is_cleaning = false,
            files = [],
            sub_operations,
        } = operation || {};
        const tasks = editMode === ACTION_TYPE.CREATE
            ? Object.keys(store || {}).reduce((rs: TTaskTreatment[], key) => {
                const { site_id: subSiteId } = store[key];
                if (subSiteId !== newSiteId)
                    return rs;
                rs.push(initTask(store[key]))
                return rs
            }, [])
            : operation?.tasks || []
        let isLock = !!currentUnitId;
        const indexes: { [id: string]: number } = {};
        const group = tasks.reduce((rs: any, item: any, index: number) => {
            const { id } = item.sub_operation || item.sub_operations[0];
            const data = { ...item, index };
            if (!rs[id]) {
                indexes[id] = index;
                rs[id] = {
                    isLock,
                    tasks: [data]
                }
            } else
                rs[id].tasks.push(data)
            if (id === currentUnitId)
                isLock = false;

            return rs;
        }, {});

        const units = site.units.reduce((rs: { [id: string]: IUnitWhenEdit }, item) => {
            const { id } = item;
            const old = (sub_operations || []).find((item: IUnit) => item.id === id) || {};
            const isSorting = checkSorting(group[id]?.tasks || []);
            rs[id] = {
                ...UnitModel(item, site),
                ...group[id] || {},
                ...old,
                isSorting,
                index: indexes[id],
                process_type: (old as IUnit).process_type || PROCESS_TYPE.SEQUENCE
            };
            return rs;
        }, {})

        const op = {
            ...operation || {},
            tenant_id,
            site_id,
            site_name,
            operation_type: OPERATION_TYPE.TREATMENT,
            certain_mode,
            is_cleaning
        } as TOperation
        const [{ est_start_time = Date.now() }] = activity_log || []
        set({
            site,
            files,
            startTime: est_start_time,
            operation: op,
            isCalculate: !op.id,
            units,
        })
    }, [get, set, sites]);

    const { vessel, editMode } = get()
    const [loadingSubmit] = useEditOp(state => state.loadingSubmit);
    const [loadingRoundMap] = useEditOp(state => state.loadingRoundMap);

    const [operation] = useEditOp(state => state.operation);
    const [activityLog] = useEditOp(state => state.activity_log);
    const [files] = useEditOp(state => state.files);
    const [units] = useEditOp(state => state.units);
    const { note = '', certain_mode = CERTAIN_TYPE.NONE, is_cleaning } = operation || {};

    const taskByUnit = useMemo(() => {
        return Object.keys(units).reduce((rs: IUnitWhenEdit[], key) => {
            const { tasks = [] } = units[key];
            if (tasks.length > 0) {
                rs.push(units[key])
            }
            return rs
        }, [])
    }, [units]);

    const dragTaskByUnit = useMemo(() => {
        const displayUnits = dragPreviewUnits || units;
        return Object.keys(displayUnits).reduce((result: IUnitWhenEdit[], unitId) => {
            const { tasks = [] } = displayUnits[unitId];
            if (tasks.length > 0 || unitId === dragSourceUnitId) result.push(displayUnits[unitId]);
            return result;
        }, []);
    }, [dragPreviewUnits, dragSourceUnitId, units]);

    const handleDragUnitEnd = useCallback((event: Parameters<NonNullable<React.ComponentProps<typeof DragDropProvider>['onDragEnd']>>[0]) => {
        setIsDraggingUnit(false);
        setDragSourceUnitId(null);
        if (event.canceled) {
            dragPreviewUnitsRef.current = null;
            setDragPreviewUnits(null);
            return;
        }

        if (!isSortableOperation(event.operation)) {
            dragPreviewUnitsRef.current = null;
            setDragPreviewUnits(null);
            return;
        }

        const sortableSource = event.operation.source;
        if (sortableSource?.type !== 'unit') {
            const previewUnits = dragPreviewUnitsRef.current;
            dragPreviewUnitsRef.current = null;
            setDragPreviewUnits(null);
            if (previewUnits && previewUnits !== get().units) {
                set({ units: previewUnits });
                turnOnCalculate();
            }
            return;
        }
        const fromIndex = sortableSource?.initialIndex;
        const toIndex = sortableSource?.index;

        if (fromIndex === undefined || toIndex === undefined || fromIndex === toIndex)
            return;

        set(({ units }) => {
            const sortableIds = Object.keys(units).filter(key => (units[key].tasks || []).length > 0);
            const [movedId] = sortableIds.splice(fromIndex, 1);
            if (!movedId)
                return {};

            sortableIds.splice(toIndex, 0, movedId);
            let sortableIndex = 0;
            const reorderedUnits = Object.keys(units).reduce((result: { [id: string]: IUnitWhenEdit }, key) => {
                if ((units[key].tasks || []).length === 0) {
                    result[key] = units[key];
                    return result;
                }

                const sortedId = sortableIds[sortableIndex++];
                result[sortedId] = units[sortedId];
                return result;
            }, {});
            return { units: normalizeParallelGroups(reorderedUnits, sortableIds) };
        });
        turnOnCalculate();
    }, [get, set, turnOnCalculate]);

    const handleDragOver = useCallback((event: Parameters<NonNullable<React.ComponentProps<typeof DragDropProvider>['onDragOver']>>[0]) => {
        const { source } = event.operation;
        if (!source || (source.type !== 'task' && source.type !== 'taskGroup')) return;

        const currentPreview = dragPreviewUnitsRef.current || units;
        const containers = flattenTaskContainers(currentPreview);
        const movedContainers = move(containers, event) as TreatmentDragContainers;
        if (movedContainers === containers) return;

        const nextUnits = rebuildUnitsFromContainers(currentPreview, movedContainers);
        Object.keys(nextUnits).forEach((unitId) => {
            nextUnits[unitId] = {
                ...nextUnits[unitId],
                isSorting: checkSorting((nextUnits[unitId].tasks || []) as TTaskTreatment[]),
            };
        });
        dragPreviewUnitsRef.current = nextUnits;
        setDragPreviewUnits(nextUnits);
    }, [units]);

    return <div id='container-treatment' className={styles['container-operation']}>
        <div className={styles['sub-plan']}>
            <div />
            <div className={styles['sub-section-right']}>
                <DensityUnits />
                <AddUnit />
                <AttachFile isShowList={false} />
                <AddNote
                    isTooltip
                    title={`#${operation?.operation_code}`}
                    value={note}
                    onChange={note => handleUpdateOp({ note })}
                />
                <Divider orientation='vertical' />
                <Certain
                    value={certain_mode}
                    onChange={certain_mode => handleUpdateOp({ certain_mode })}
                />
            </div>
        </div>
        <div className={styles['sub-files']} data-is-show={files.length > 0}>
            <Files />
        </div>
        {(note || '').length > 0 && <div className='px-[24px]'>
            <Note {...{ isEdit: true, note }} handleUpdate={note => handleUpdateOp({ note })} />
        </div>}
        <div className={[styles['wrapper-operation'], 'scroll-small'].join(' ')}>
            {dragTaskByUnit.length === 0 && <div className={styles.line} data-lev='empty'>No task</div>}
            <DragDropProvider
                onDragStart={(event) => {
                    const { source } = event.operation;
                    setIsDraggingUnit(source?.type === 'unit');
                    const isTaskDrag = source?.type === 'task' || source?.type === 'taskGroup';
                    const sourceUnitId = isTaskDrag ? findTaskUnitId(units, String(source?.id)) : undefined;
                    setDragSourceUnitId(sourceUnitId || null);
                    dragPreviewUnitsRef.current = isTaskDrag ? units : null;
                    setDragPreviewUnits(isTaskDrag ? units : null);
                }}
                onDragOver={handleDragOver}
                onDragEnd={handleDragUnitEnd}
            >
                <div id='treatment-operation' className={styles.operation}>
                    {dragTaskByUnit.map((item, index) => <GroupByUnit
                        key={item.id}
                        unit={item}
                        index={index}
                        isDraggingUnit={isDraggingUnit}
                    />)}
                </div>
                <DragOverlay dropAnimation={null}>
                    {source => {
                        const displayUnits = dragPreviewUnits || units;
                        const unit = displayUnits[String(source.id)];
                        if (!unit) {
                            const task = findTaskById(displayUnits, String(source.id));
                            if (!task) return null;

                            return <div
                                className={styles['task-drag-overlay']}
                                style={{ width: source.element?.getBoundingClientRect().width }}
                            >
                                <div className={styles['task-drag-content']}>
                                    {getTaskDragLabel(task)}
                                </div>
                            </div>;
                        }

                        return <div
                            className={styles['unit-drag-overlay']}
                            style={{ width: source.element?.getBoundingClientRect().width }}
                        >
                            <div>{[unit.site_name, unit.unit_id].filter(Boolean).join(', ')}</div>
                            {!!unit.density_percent && <div>
                                Density: {Math.round(unit.density_percent * 100)}%
                            </div>}
                        </div>
                    }}
                </DragOverlay>
            </DragDropProvider>
            {editMode === ACTION_TYPE.EDIT && operation && vessel && <div className="px-[24px] pb-3 ">
                <Invoices
                    operation={operation}
                    activityLog={activityLog}
                    operations={operations}
                    vesselId={vessel.id}
                    onChange={invoices => handleUpdateOp({ invoices })}
                />
            </div>}
            <div className='flex items-center justify-between mb-2'>
                <div className='flex gap-3 items-center'>
                    <SelectTime extend={() => handleGetRoundMap()} />
                    <Duration />
                </div>
                <div className='flex items-center pr-4 gap-4'>
                    {operation &&
                        <AddChecklist data={operation} onChange={value => handleUpdateOp({ checklist_ids: value })} />}
                    {taskByUnit.length > 0 && <Cleaning
                        is_cleaning={!!is_cleaning}
                        onChange={is_cleaning => handleUpdateOp({ is_cleaning })}
                    />}
                </div>
            </div>
            {vessel?.type !== VESSEL_TYPE.SERVICE && <RoundMapProvider isEdit>
                <RoundMap units={taskByUnit} />
            </RoundMapProvider>}
        </div>
        <div className='mt-auto'>
            <Footer {...{
                disableSave: taskByUnit.length === 0 || loadingRoundMap
                    || taskByUnit.some(unit => !unit.fish_amount || !unit.total_weight
                        || checkTreatment((unit.tasks || []) as TTaskTreatment[]).isError),
                loading: loadingSubmit,
                handleSubmit,
                handleBeforePlan: action => isCalculate ? getActivityLogOfTreatment(action) : action?.()
            }} />
        </div>
    </div>
};

export default Treatment
